import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { getModeDisplay } from '../helper';
import type { IPayoutData, WithdrawMode } from '../../../interfaces/Withdrawal';
import type { IUserDetails } from '../../../storage/store';
import styles from '../styles.module.css';

interface ModeRowProps {
  payoutData?: IPayoutData;
  userDetails?: IUserDetails;
  availableModes: WithdrawMode[];
  selectedMode?: WithdrawMode;
  onSelectMode: (mode: WithdrawMode) => void;
  onAddMode: () => void;
  onManageModes: () => void;
}

/**
 * The payout destination. With modes saved it shows the selected one and lets
 * the user switch; with none it falls back to the server's `noModeText` and
 * sends the user to add one.
 */
const ModeRow: React.FC<ModeRowProps> = ({
  payoutData,
  userDetails,
  availableModes,
  selectedMode,
  onSelectMode,
  onAddMode,
  onManageModes,
}) => {
  const { t } = useTranslation();
  const modeData = payoutData?.modeData;

  if (!availableModes.length) {
    return (
      <button type="button" className={styles.modeRowEmpty} onClick={onAddMode}>
        <span className={clsx(styles.modeEmptyText, 'fontSizeSm14')}>
          {modeData?.noModeText || t('noWithdrawModeText')}
        </span>
        <span className={clsx(styles.modeAction, 'fontSizeSm14 fontWeightBold')}>
          {t('addLabel')}
        </span>
      </button>
    );
  }

  const { value } = getModeDisplay(userDetails, selectedMode);
  // With more than one usable mode the row toggles between them; with one, the
  // action opens the saved-modes screen so the account can be changed there.
  const canSwitch = availableModes.length > 1;

  const handleSwitch = () => {
    const next = availableModes.find((mode) => mode !== selectedMode);
    if (next) onSelectMode(next);
  };

  return (
    <div className={styles.modeRow}>
      <div className={styles.modeInfo}>
        <div>
          <p className={clsx(styles.modeLabel, 'fontSizeSm12 fontColor666')}>
            {modeData?.modeSelectionData?.title || t('withdrawToLabel')}
          </p>
          <p className={clsx(styles.modeValue, 'fontSizeSm14 fontWeightBold')}>
            {value}
          </p>
        </div>
      </div>

      <button
        type="button"
        className={clsx(styles.modeAction, 'fontSizeSm14 fontWeightBold')}
        onClick={canSwitch ? handleSwitch : onManageModes}
      >
        {modeData?.editModeText || t('changeLabel')}
      </button>
    </div>
  );
};

export default ModeRow;
